/**
 * useMazeRunner.ts
 * 迷路アプリの実行状態（ロボットの座標・向き）を管理し、
 * フロー実行エンジン(run.ts)から呼び出される ExecContext.mazeControl を提供する
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { measureMazeWallDistanceMm, type MazeStageDefinition } from '../../flow/config/mazeStages';
import type { ExecContext } from '../../flow/types';
import type { MazeRobotPose } from '../../flow/animation/useWegoAnimationScene';
import { applyMazeWalkingMotion, isMazeGoalPosition } from './mazeCore';

export function useMazeRunner(stage: MazeStageDefinition, onGoalReached?: () => void) {
  const initialPose = { x: stage.start.x, y: stage.start.y, dir: stage.start.dir };
  const [pose, setPose] = useState<MazeRobotPose>(initialPose);
  const poseRef = useRef<MazeRobotPose>(initialPose);
  const goalReachedRef = useRef(false);
  const onGoalReachedRef = useRef(onGoalReached);
  onGoalReachedRef.current = onGoalReached;

  /** ステージのスタート地点へロボットを戻す（編集モードへ戻る・再挑戦時に使用） */
  const resetPose = useCallback(() => {
    poseRef.current = { x: stage.start.x, y: stage.start.y, dir: stage.start.dir };
    goalReachedRef.current = false;
    setPose(poseRef.current);
  }, [stage]);

  const commitPose = useCallback((nextPose: MazeRobotPose) => {
    poseRef.current = nextPose;
    setPose(nextPose);
  }, []);

  const mazeControl = useMemo<NonNullable<ExecContext['mazeControl']>>(() => ({
    isAtGoal: () => isMazeGoalPosition(stage, poseRef.current),
    measureDistanceMm: (maxDistanceMm) => measureMazeWallDistanceMm(stage, poseRef.current, maxDistanceMm),
    canApplyWalkingMotion: (motionNo, displacement) => (
      !goalReachedRef.current && !applyMazeWalkingMotion(stage, poseRef.current, motionNo, displacement).blocked
    ),
    applyWalkingMotion: async (motionNo, displacement) => {
      if (goalReachedRef.current) {
        return { blocked: true, goalReached: true };
      }
      const current = poseRef.current;
      const result = applyMazeWalkingMotion(stage, current, motionNo, displacement);
      if (!result.blocked) commitPose(result.pose);
      if (result.goalReached) {
        goalReachedRef.current = true;
        onGoalReachedRef.current?.();
      }
      return { blocked: result.blocked, goalReached: result.goalReached };
    },
  }), [stage, commitPose]);

  return { mazeControl, resetPose, pose };
}
